/**
 * LazyImage Component
 * Implements lazy loading for images using Intersection Observer
 * Loads images only when they enter the viewport
 * Shows blur placeholder during loading
 * Feature: Performance optimization for presenter photos
 */

import { useState, useEffect, useRef } from 'react';
import '../styles/LazyImage.css';

const LazyImage = ({ src, alt, className, fallback }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Set up Intersection Observer
  useEffect(() => {
    if (!imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect(); // Stop observing once loaded
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
        threshold: 0.01, // Trigger as soon as 1% is visible
      }
    );

    observer.observe(imgRef.current);

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, []);

  // Handle image load
  const handleLoad = () => {
    setIsLoaded(true);
  };

  // Handle image error
  const handleError = () => {
    setHasError(true);
  };

  return (
    <div ref={imgRef} className={`lazy-image-wrapper ${className || ''}`}>
      {!hasError ? (
        <>
          {/* Blur placeholder */}
          {!isLoaded && isInView && (
            <div className="lazy-image-placeholder">
              <div className="lazy-image-spinner"></div>
            </div>
          )}

          {/* Actual image - only load when in view */}
          {isInView && (
            <img
              src={src}
              alt={alt}
              className={`lazy-image ${isLoaded ? 'lazy-image-loaded' : 'lazy-image-loading'}`}
              onLoad={handleLoad}
              onError={handleError}
            />
          )}

          {/* Show placeholder before in view */}
          {!isInView && (
            <div className="lazy-image-placeholder">
              <div className="lazy-image-icon">&#128444;</div>
            </div>
          )}
        </>
      ) : (
        /* Show fallback on error */
        fallback || (
          <div className="lazy-image-fallback">
            <div className="presenter-placeholder">👤</div>
          </div>
        )
      )}
    </div>
  );
};

export default LazyImage;
